<template>
  <section class="analytics-creator-table">
    <div class="sticky-header">
      <div class="list-header">
        <div class="ui-search-wrap">
          <Search @onenter="onSearch" :disabled="is_loading" placeholder_text="Search by creator name or social handle" />
        </div>
        <div class="list-btn-box">
          <FilterDropdown
            :params="filter_params"
            :direction_type="'right'"
            :hide_social_followers="true"
            @update-filter="updateFilter"
          />
          <Dropdown
            :items="per_page_items"
            :direction_type="'right'"
            :no_icon="true"
            :other_text="''"
            @update-dropdown="onPerPageChange"
          />
          <Pagenavi
            :disabled="last_page === 1"
            :page_current="current_page"
            :page_total="last_page"
            @goto-page="gotoPage"
          />
          <div
            class="download-btn"
            :class="{ 'is-disabled': is_downloading || safe_data.length === 0 }"
            v-tippy="{ content: 'Download creator performance in CSV format', delay: [500, 20] }"
            @click="downloadCsv"
          >
            <Loading v-if="is_downloading" class="small" />
            <svg-icon v-else name="download2" />
          </div>
        </div>
      </div>
    </div>

    <Loading v-if="is_loading && !data.length" class="static" />

    <Scrollbar class="data-list-scroll" :show_bar="true">
      <div v-if="safe_data.length !== 0" class="creator-table-view">
        <Loading v-if="is_loading" class="absolute" />
        <Table>
          <template #head>
            <tr>
              <th class="name-row">
                <span>Name ({{ total }})</span>
              </th>
              <TableTh
                name="total_products"
                title="Total Products"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Total Products"
              />
              <TableTh
                v-if="!is_link_only"
                name="views"
                title="Product Views"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Views"
              />
              <TableTh
                name="click"
                title="Product Clicks"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Product Clicks"
              />
              <TableTh name="sales" title="Sales" :sort_value="sort" @sort="onColumnSort" content="Sales" />
              <TableTh
                name="sales_value"
                title="Sales Value"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Sales Value"
              />
              <TableTh
                name="earnings"
                title="Earnings"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Earnings"
              />
              <TableTh
                name="last_update"
                title="Last Update"
                :sort_value="sort"
                @sort="onColumnSort"
                content="Last Update"
              />
            </tr>
          </template>

          <template #body>
            <tr v-for="(item, index) in safe_data" :key="index" @click.stop="openDetail(item)" style="cursor: pointer;">
              <td class="name-row">
                <div class="creator-cell">
                  <img
                    class="creator-avatar"
                    :src="item.userProfile?.avatar_full_url || require('@/assets/images/user.png')"
                    @error="(e) => e.target.src = require('@/assets/images/user.png')"
                  />
                  <div class="creator-info">
                    <div class="creator-name">{{ item.first_name ? `${item.first_name} ${item.last_name}` : item.name }}</div>
                    <span
                      v-if="item.anchor_page_status != null"
                      :class="['status-tag', 'class-' + item.anchor_page_status]"
                    >
                      <i class="status-dot"></i>{{ item.anchor_page_status_text }}
                    </span>
                  </div>
                </div>
              </td>
              <td>{{ item.total_products != null ? item.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!is_link_only">{{ item.product_views ? Number(item.product_views).toLocaleString('en-US') : item.views ? Number(item.views).toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.click ? Number(item.click).toLocaleString('en-US') : '---' }}</td>
              <td>{{ item.sales ? item.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!isNaN(item.sales_value)">
                ${{ Number(item.sales_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="!isNaN(item.earnings)">
                ${{ Number(item.earnings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td>{{ item.updated_at ? formatDate(item.updated_at) : '---' }}</td>
            </tr>
          </template>

          <template #foot>
            <tr class="total-row" v-if="data_total">
              <td class="name-row">Total: {{ total }}</td>
              <td>{{ data_total.total_products != null ? data_total.total_products.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!is_link_only">{{ data_total.views ? Number(data_total.views).toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.click ? Number(data_total.click).toLocaleString('en-US') : '---' }}</td>
              <td>{{ data_total.sales ? data_total.sales.toLocaleString('en-US') : '---' }}</td>
              <td v-if="!isNaN(data_total.sales_value)">
                ${{ Number(data_total.sales_value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td v-if="!isNaN(data_total.earnings)">
                ${{ Number(data_total.earnings).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }}
              </td>
              <td v-else>---</td>
              <td>---</td>
            </tr>
          </template>
        </Table>
      </div>

      <div v-if="!is_loading && data.length === 0">
        <NoData :image="require('@/assets/images/pc.png')" title="No data available" text="" />
      </div>
    </Scrollbar>
  </section>
</template>

<script setup>
import { ref, reactive, computed, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import { useAnalyticsProductList, per_page_items } from '@/composables/useAnalyticsProductList.js';
import Scrollbar from '@/components/ui/Scrollbar.vue';
import Search from '@/components/ui/Search.vue';
import Dropdown from '@/components/ui/Dropdown.vue';
import FilterDropdown from '@/components/FilterDropdown.vue';
import Pagenavi from '@/components/ui/Pagenavi.vue';
import Loading from '@/components/ui/Loading.vue';
import NoData from '@/components/creator/NoData.vue';
import Table from '@/components/ui/Table.vue';
import TableTh from '@/components/ui/TableTh.vue';
import { useNotification } from '@/components/ui/notification';

const props = defineProps({
  build_statistic_url: { type: Function, required: true },
  build_list_url: { type: Function, required: true },
  build_csv_url: { type: Function, required: false, default: null },
  is_advertiser: { type: Boolean, default: false },
});

const { openNotBottom } = useNotification();
const store = useStore();
const { proxy } = getCurrentInstance();
const EventBus = proxy.EventBus;
const $JsDownload = proxy.$JsDownload;
const $notify = proxy.$notify;

const openDetail = (item) => {
  EventBus.emit('open-creator-modal', {
    user_id: item.anchor_creator_id ?? item.id,
    start_date: store.state.save_analytics_start_date,
    end_date: store.state.save_analytics_end_date,
  });
};

const {
  is_loading, data, data_total,
  current_page, last_page, total,
  sort,
  is_link_only,
  onSearch, onColumnSort,
  onProductAddedChange, onAccountStatusChange, onCreatorTypeChange,
  batchFilterUpdate,
  onPerPageChange, gotoPage,
  fetchExportCsv,
} = useAnalyticsProductList({
  buildStatisticUrl: props.build_statistic_url,
  buildListUrl: props.build_list_url,
  // Parent passes its own CSV URL builder since the CSV endpoint's path doesn't
  // reliably derive from the list endpoint's path (naming isn't consistent across modules).
  buildCsvUrl: props.build_csv_url || undefined,
  // unlike AdBrandTable/AdAdvertiserTable/AdBrandProductTable/AdProductTable, this
  // component never renders data_statistic — skip fetching it (it was pure dead
  // weight here, and duplicated the host modal's own statistic call for its tab's
  // top summary cards)
  includeStatistic: false,
});

const safe_data = computed(() => Array.isArray(data.value) ? data.value.filter(i => i != null) : []);

const filter_params = reactive({
  profile_status: 0,
  storefront_status: 0,
  recently_added: 0,
  metric: 0,
  metric_type: 0,
  metric_amount: '',
  metric_min_amount: '',
  metric_max_amount: '',
  account_type: 0,
  social: { metric: 1, metric_msg: '', metric_min_amount_input: '', metric_max_amount_input: '' },
});

const updateFilter = (params) => {
  Object.assign(filter_params, params);
  batchFilterUpdate({
    product_added: params.recently_added === 1 ? 'recently_added' : '',
    account_status: { 1: 'active', 3: 'disabled' }[params.storefront_status] ?? '',
    creator_type: { 1: 'storefront', 2: 'linkonly' }[params.account_type] ?? '',
  });
};

const formatDate = (val) => {
  if (!val) return '---';
  const d = new Date(val);
  return isNaN(d.getTime()) ? '---' : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const is_downloading = ref(false);

const downloadCsv = async () => {
  if (is_downloading.value || safe_data.value.length === 0) return;
  is_downloading.value = true;
  try {
    const res = await fetchExportCsv();
    let name = 'creator-performance';
    const disposition = res.headers?.['content-disposition'];
    if (disposition && disposition.includes('filename=')) {
      name = disposition.split('filename=')[1].replace(/["';]/g, '').trim() || name;
    }
    $JsDownload(res.data, name.endsWith('.csv') ? name : `${name}.csv`);
    openNotBottom('Download Success!', "success");
  } catch (e) {
    openNotBottom('Download Failed!', "error");
  } finally {
    is_downloading.value = false;
  }
};
</script>

<style lang="scss">
.analytics-creator-table {
  padding-bottom: 24px;

  .sticky-header {
    position: sticky;
    top: 0;
    background: white;
    z-index: 20;
  }

  .list-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 13px 0;

    .list-btn-box {
      display: flex;
      align-items: center;
      gap: 8px;
      margin-left: auto;
    }
  }

  .download-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    border: 1px solid var(--black-20);
    border-radius: 6px;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color .2s ease, border-color .2s ease;
    position: relative;

    .svg-icon {
      width: 14px;
      height: 14px;
      color: var(--black-60);
    }

    .loading.small {
      position: absolute;
      top: 50%;
      left: 50%;
      width: auto;
      transform: translate(-50%, -50%) scale(0.32);

      > div {
        height: auto;
      }
    }

    &:hover {
      border-color: var(--black-40);
      background-color: var(--bg-02);
    }

    &.is-disabled {
      cursor: not-allowed;
      opacity: .5;
      pointer-events: none;
    }
  }

  .ui-search-wrap {
    flex: 1;

    .ui-search-input .input {
      max-width: unset;
    }
  }

  .data-list-scroll {
    overflow-y: visible;
    height: calc(98vh - 360px);
  }

  .creator-table-view {
    margin-top: 8px;
    cursor: pointer;
    position: relative;

    .ui-table thead {
      position: sticky;
      top: 0;
      background: white;
      z-index: 10;
    }

    .name-row {
    }

    .creator-cell {
      display: flex;
      align-items: center;
      gap: 10px;

      .creator-avatar {
        width: 36px;
        height: 36px;
        border-radius: 50%;
        object-fit: cover;
        flex-shrink: 0;
      }

      .creator-info {
        min-width: 0;
        display: flex;
        flex-direction: column;
        gap: 2px;

        .creator-name {
          font: var(--font-fg-13);
          color: var(--black-90);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
      }
    }

    .status-tag {
      display: inline-flex;
      align-items: center;
      font: var(--font-fg-12);
      color: var(--black-60);

      .status-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        margin-right: 4px;
        display: inline-block;
      }

      &.class-100 .status-dot { background: var(--green); }
      &.class-20 .status-dot { background: var(--yellow); }
      &.class-0 .status-dot { background: var(--black-40); }
    }
  }

  .tfoot tr td {
    vertical-align: top;
  }
}
</style>
